import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { FRAMEWORKS, type FrameworkId } from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';

interface FrameworkAssessment {
  frameworkId: FrameworkId;
  answeredCount: number;
  totalCount: number;
  isComplete: boolean;
}

export function AssessmentOverview() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [assessments, setAssessments] = useState<FrameworkAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchAssessments();
  }, [currentWorkspace]);

  async function fetchAssessments() {
    if (!currentWorkspace) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<FrameworkAssessment[]>(
      `/api/tenants/${currentWorkspace.tenantId}/assessments`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setAssessments(data);
    }
    setLoading(false);
  }

  const totalQuestions = assessments.reduce((sum, a) => sum + a.totalCount, 0);
  const totalAnswered = assessments.reduce((sum, a) => sum + a.answeredCount, 0);
  const overallPct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">Assessments</h1>
        <p className="text-slate-400 text-sm mt-1">
          Overall progress: {overallPct}%
        </p>
      </div>

      {/* Overall progress bar */}
      {assessments.length > 0 && (
        <div className="bg-surface-mid rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-300">Overall Completion</span>
            <span className="text-sm font-medium text-white">{overallPct}%</span>
          </div>
          <div className="h-2 bg-surface-dark rounded-full overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all"
              style={{ width: `${overallPct}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {totalAnswered} of {totalQuestions} questions answered across {assessments.length} framework{assessments.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Framework assessment list */}
      {assessments.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">No active frameworks found.</p>
            <p className="text-slate-500 text-xs mt-1">
              Activate frameworks first, then return here to complete assessments.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={() => navigate('/frameworks')}
            >
              Go to Frameworks
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {assessments.map((assessment) => {
            const fw = FRAMEWORKS[assessment.frameworkId];
            if (!fw) return null;

            const pct = assessment.totalCount > 0
              ? Math.round((assessment.answeredCount / assessment.totalCount) * 100)
              : 0;

            let buttonLabel = 'Start';
            let buttonVariant: 'primary' | 'secondary' | 'ghost' = 'primary';
            if (assessment.isComplete) {
              buttonLabel = 'Completed';
              buttonVariant = 'ghost';
            } else if (assessment.answeredCount > 0) {
              buttonLabel = 'Continue';
              buttonVariant = 'secondary';
            }

            return (
              <div
                key={assessment.frameworkId}
                className="bg-surface-mid rounded-xl border border-border p-5 flex items-center gap-5"
              >
                {/* Framework info */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: fw.color }}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{fw.name}</p>
                    <p className="text-xs text-slate-500">{fw.shortName}</p>
                  </div>
                </div>

                {/* Progress */}
                <div className="w-40 shrink-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">
                      {assessment.answeredCount}/{assessment.totalCount}
                    </span>
                    <span className="text-xs text-slate-400">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-surface-dark rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        assessment.isComplete ? 'bg-growth' : 'bg-brand'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                {/* Status / Action */}
                <div className="shrink-0">
                  {assessment.isComplete ? (
                    <Badge variant="success">Completed</Badge>
                  ) : (
                    <Button
                      variant={buttonVariant}
                      size="sm"
                      onClick={() => navigate(`/assessments/${assessment.frameworkId}`)}
                    >
                      {buttonLabel}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
